import React, { useEffect, useRef, useState, useMemo, useCallback } from "react";
import debounce from "lodash.debounce";
import t from "Utils/translation";
import s from "./auditAutocompleteSelect.scss";
import cx from "classnames";
/**
 * AutocompleteSelect component
 *
 * @param {Object} props - Component props
 * @param {({ email: string, code: string, header?: boolean, type?: string }) => void} props.onChange - Callback called when a value is selected or cleared
 * @param {(input: string) => Promise<Array<{ email: string, code: string, header?: boolean, type?: string }>>} props.loadOptions - Async function to load options based on input
 * @param {boolean} [props.triggerClear=false] - If true, the selection will be cleared
 * @param {string} [props.placeholder="Select..."] - Input placeholder text
 * @param {number} [props.minLetters=4] - Minimum number of letters to trigger loading options
 * @param {string} [props.initValue] - Initial value to be used for loading options
 * @param {string} [props.label] - Optional label for the input field
 * @param {string} [props.className] - Optional external classes
 */
export const AuditAutocompleteSelect = ({
  onChange,
  loadOptions,
  triggerClear = false,
  placeholder = t("input.placeholder.filters"),
  minLetters = 4,
  initValue,
  label,
  className = "",
}) => {
  const [query, setQuery] = useState("");
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [isInputFocused, setIsInputFocused] = useState(false);
  const [isUsingKeyboard, setIsUsingKeyboard] = useState(false);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const wrapperRef = useRef(null);

  const optionRefs = useRef([]);

  const loadOptionsHandler = useCallback(
    async (input) => {
      setLoading(true);
      const res = await loadOptions(input);
      setOptions(res ?? []);
      setLoading(false);
      setHighlightedIndex(1);
    },
    [loadOptions]
  );
  const debouncedLoadOptions = useMemo(() => debounce(loadOptionsHandler, 300), [loadOptionsHandler]);

  const loadInitialOptions = useCallback(
    async (input) => {
      setIsInitialLoading(true);
      setLoading(true);
      const res = await loadOptions(input);
      setLoading(false);
      setIsInitialLoading(false);

      const filteredOptions = res?.filter((item) => !item.header) ?? [];
      const first = filteredOptions[0];
      if (first) {
        setSelected(first);
        setQuery(first.email);
        setOptions(res ?? []);
      }
    },
    [loadOptions]
  );

  const handleInputChange = (e) => {
    const val = e.target.value;

    if (query !== val && selected) {
      onChange(null);
    }

    setQuery(val);

    setSelected(null);

    if (val.trim().length < minLetters) {
      debouncedLoadOptions.cancel();
      setOptions([]);

      return;
    }
    debouncedLoadOptions(val);
  };

  const handleOptionSelect = (option) => {
    setSelected(option);
    setQuery(option.email);
    onChange(option);
    setIsInputFocused(false);
  };

  const handleClearSelection = useCallback(() => {
    setQuery("");
    setSelected(null);
    setOptions([]);
    onChange(null);
  }, []);

  const handleKeyDown = (e) => {
    setIsUsingKeyboard(true);
    const getNextIndex = (current, direction) => {
      const step = direction === "down" ? 1 : -1;
      const start = current + step;

      for (let i = start; i >= 0 && i < options.length; i += step) {
        if (!options[i].header) {
          return i;
        }
      }

      return current;
    };
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightedIndex((prev) => getNextIndex(prev < 0 ? -1 : prev, "down"));
    }

    if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightedIndex((prev) => getNextIndex(prev <= 0 ? 0 : prev, "up"));
    }

    if (e.key === "Enter" && highlightedIndex >= 0 && options[highlightedIndex] && !options[highlightedIndex].header) {
      e.preventDefault();
      handleOptionSelect(options[highlightedIndex]);
      if (inputRef.current) {
        inputRef.current.blur();
      }
    }
  };

  useEffect(() => {
    handleClearSelection();
  }, [triggerClear, handleClearSelection]);

  useEffect(() => {
    if (highlightedIndex >= 0 && optionRefs.current[highlightedIndex]) {
      optionRefs.current[highlightedIndex].scrollIntoView({ block: "nearest" });
    }
  }, [highlightedIndex]);

  useEffect(() => {
    if (initValue) {
      loadInitialOptions(initValue);
    }
  }, []);

  useEffect(() => {
    return () => {
      debouncedLoadOptions.cancel();
    };
  }, [debouncedLoadOptions]);

  useEffect(() => {
    if (!isUsingKeyboard) return;
    const timer = setTimeout(() => setIsUsingKeyboard(false), 1000);
    return () => clearTimeout(timer);
  }, [highlightedIndex, isUsingKeyboard]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      const clickedOutside =
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target) &&
        listRef.current &&
        !listRef.current.contains(event.target);

      if (clickedOutside) {
        setIsInputFocused(false);

        if (!selected) {
          setSelected(null);
          setQuery("");
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const input = inputRef.current;
    if (!input) return;

    const handleFocus = () => {
      setIsInputFocused(true);
      setLoading(true);
      debouncedLoadOptions(query);
      const firstIndex = options.findIndex((o) => !o.header);
      setHighlightedIndex(firstIndex);
    };

    const handleBlur = (e) => {
      if (listRef.current && listRef.current.contains(e.relatedTarget)) {
        return;
      }
      setIsInputFocused(false);
    };

    input.addEventListener("focus", handleFocus);
    input.addEventListener("blur", handleBlur, true);

    return () => {
      input.removeEventListener("focus", handleFocus);
      input.removeEventListener("blur", handleBlur, true);
    };
  }, [debouncedLoadOptions, options, query, selected]);

  return (
    <div className={`relative ${className} `} ref={wrapperRef} onMouseMove={() => setIsUsingKeyboard(false)}>
      {label && (
        <label htmlFor="input" className={s.label}>
          {label}
        </label>
      )}
      <div className={`${s.inputContainer} relative`}>
        <input
          id="input"
          type="text"
          ref={inputRef}
          value={query}
          placeholder={placeholder}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          role="combobox"
          aria-autocomplete="list"
          aria-expanded={isInputFocused}
          aria-controls="autocomplete-list"
          aria-activedescendant={highlightedIndex >= 0 ? `autocomplete-option-${highlightedIndex}` : undefined}
          className={`${s.input} ${
            isInitialLoading ? "bg-gray-100 animate-pulse text-transparent caret-transparent" : ""
          }`}
        />
        {isInitialLoading && (
          <div className="absolute inset-y-0 right-2 flex items-center">
            <div className="w-4 h-4 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin" />
          </div>
        )}
      </div>
      {isInputFocused && (
        <>
          {query.trim().length < minLetters ? (
            <div className={s.searchMsg}>{t("input.placeholder.audit.searchMsg")}</div>
          ) : loading ? (
            <div className={s.searchMsg}>{t("input.placeholder.Loading")}.</div>
          ) : options.length === 0 ? (
            <div className={cx(s.searchMsg, s.errorMsg)}>{`${t("input.placeholder.noMatchess")}${query}`}</div>
          ) : (
            <ul
              id="autocomplete-list"
              role="listbox"
              ref={listRef}
              tabIndex={0}
              onMouseLeave={() => setIsUsingKeyboard(false)}
              className={s.menu}
            >
              {options.map((opt, i) => {
                if (opt.header) {
                  return (
                    <li key={`header-${opt.type}-${i}`} className={s.elementHeader}>
                      {opt.type}
                    </li>
                  );
                }

                return (
                  <li
                    id={`autocomplete-option-${i}`}
                    role="option"
                    aria-selected={highlightedIndex === i}
                    ref={(el) => (optionRefs.current[i] = el)}
                    key={opt.code + i}
                    tabIndex={0}
                    className={`${s.itemElement}  ${i === highlightedIndex && s.highlightItem}`}
                    onMouseEnter={(e) => {
                      if (!isUsingKeyboard) {
                        setHighlightedIndex(i);
                      } else {
                        e.preventDefault();
                        e.stopPropagation();
                      }
                    }}
                    onMouseDown={() => handleOptionSelect(opt)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && highlightedIndex === i) {
                        e.preventDefault();
                        handleOptionSelect(opt);
                      }
                    }}
                  >
                    <div className="flex flex-col ">
                      <div className={s.mainInfo}>{opt.fullname || opt.email}</div>
                      <div className={s.secondaryInfo}>{opt.code}</div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </>
      )}
    </div>
  );
};
